import { ExcelExport, ExcelExportColumnProps } from "@progress/kendo-react-excel-export";
import { State, translateDataSourceResultGroups } from "@progress/kendo-data-query";
import axios from "axios";
import { useRef, useState } from "react";
import { faUpRightFromSquare } from "@fortawesome/free-solid-svg-icons";
import { IconWithRightMargin } from "../../Layout/Icons/IconWithRightMargin";
import { UppercaseButton } from "../../Layout/Buttons/UppercaseButton";
import { KENDO_BUTTON_CLASSES } from "../../Layout/Buttons/KendoButtons";
import { toRequestStringWithoutPage } from "../Extensions/kendoDataState";
import {
  showActionFailureToast,
  showActionSuccessToast,
  showNotSelfClosingActionStartToast,
  ToastId,
} from "../../TransportNetwork/NotificationForActionButton";
import {
  EXCEL_EXPORT_FAILURE_MESSAGE,
  EXCEL_EXPORT_START_MESSAGE,
  EXCEL_EXPORT_SUCCESS_MESSAGE,
} from "./excelExportMessages";

function getData<T>(responseData: any, dataState: State, exportMapping?: (item: T) => any) {
  const mappedData = exportMapping ? responseData.Data.map(exportMapping) : responseData.Data;
  const hasGroups = dataState.group && dataState.group.length;
  return hasGroups ? translateDataSourceResultGroups(mappedData) : mappedData;
}

interface ExcelExportButtonProps<T, TExport> {
  readonly url: string;
  readonly dataState: State;
  readonly columns: ExcelExportColumnProps[];
  readonly fileName: string;
  readonly buttonText?: string;
  readonly exportMapping?: (item: T) => TExport;
}

export default function ExcelExportButton<T, TExport>({
  url,
  dataState,
  columns,
  fileName,
  buttonText = "EXPORT TABLE",
  exportMapping,
}: ExcelExportButtonProps<T, TExport>) {
  const ExcelExportRef = useRef<ExcelExport>(null);
  const [loading, setLoading] = useState(false);
  const toastId: ToastId = useRef(null);

  const excelExport = () => {
    if (ExcelExportRef.current === null) {
      return;
    }

    const dataStateAsQueryString = toRequestStringWithoutPage(dataState);
    setLoading(true);
    toastId.current = showNotSelfClosingActionStartToast(EXCEL_EXPORT_START_MESSAGE);
    axios
      .post(url, dataStateAsQueryString)
      .then((res) => {
        const data = getData(res.data, dataState, exportMapping);
        ExcelExportRef?.current?.save(data, columns);
        showActionSuccessToast(toastId, EXCEL_EXPORT_SUCCESS_MESSAGE);
      })
      .catch(() => {
        showActionFailureToast(toastId, EXCEL_EXPORT_FAILURE_MESSAGE);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <ExcelExport ref={ExcelExportRef} fileName={fileName}>
      <UppercaseButton
        type="button"
        title={buttonText}
        className={KENDO_BUTTON_CLASSES}
        onClick={() => excelExport()}
        disabled={loading}
      >
        <IconWithRightMargin icon={faUpRightFromSquare} />
        {buttonText}
      </UppercaseButton>
    </ExcelExport>
  );
}
